import { create } from 'zustand';

import { CareServiceOrgansKey, TextToStructuredObservation } from '@/api';
import { FindingsByOrgan, UpdateFindingDetails } from '@/shared/model';

import { FindingsState } from './use-finding.model';

export const useFindingStore = create<FindingsState>((set, get) => ({
  addFinding: (finding: TextToStructuredObservation) => {
    set((state) => {
      const updatedFindings = { ...state.findings };
      const organ = finding.conditionTemplate.organ as CareServiceOrgansKey;
      const existingFindingIndex = updatedFindings[organ]?.findIndex(
        (findingData) => findingData.renderedSummary.uuid === finding.renderedSummary.uuid,
      );

      if (existingFindingIndex !== undefined && existingFindingIndex >= 0) {
        updatedFindings[organ] = [...updatedFindings[organ]];
        updatedFindings[organ][existingFindingIndex] = finding;
      } else {
        if (!updatedFindings[organ]) {
          updatedFindings[organ] = [];
        }

        updatedFindings[organ] = [...updatedFindings[organ], finding];
      }

      return {
        ...state,
        findings: updatedFindings,
      };
    });

    return get().findings;
  },
  deleteFinding: (organ: CareServiceOrgansKey, uuid: string) => {
    set((state) => {
      const organFindings = state.findings[organ] || [];
      const updatedFindings = organFindings.filter(
        (finding) => finding.renderedSummary.uuid !== uuid,
      );

      return {
        findings: {
          ...state.findings,
          [organ]: updatedFindings,
        },
      };
    });

    return get().findings;
  },
  findings: {} as FindingsByOrgan,
  getFindingByConditionTemplate: (
    organ: CareServiceOrgansKey,
    conditionTemplate: string,
    renderedSummaryUuid: string,
  ) => {
    const findings = get().getFindingsByOrgan(organ);

    return (
      findings.find(
        (finding) =>
          finding.conditionTemplate.condition === conditionTemplate &&
          finding.renderedSummary.uuid === renderedSummaryUuid,
      ) || null
    );
  },
  getFindingsByOrgan: (organ: CareServiceOrgansKey) => get().findings[organ] || [],
  updateFinding: (
    organ: CareServiceOrgansKey,
    updatedFinding: UpdateFindingDetails,
    renderedSummaryId: string,
  ) => {
    set((state) => {
      const organFindings = state.findings[organ]?.map((finding) => {
        const isSameUuid = finding.renderedSummary.uuid === renderedSummaryId;

        if (isSameUuid) {
          return {
            ...finding,
            ...updatedFinding,
          };
        }

        return finding;
      });

      return {
        ...state,
        findings: {
          ...state.findings,
          [organ]: organFindings || [updatedFinding],
        },
      };
    });

    return get().findings;
  },
}));
